import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { leadsAPI, candidatesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Inbox, Mail, Phone, Briefcase, FileText, Sparkles, Trash2, Clock, ExternalLink } from 'lucide-react';

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function LeadsInboxPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await leadsAPI.list();
      setLeads(data || []);
    } catch (e) {
      toast.error('Could not load applications');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (lead) => {
    setBusyId(lead.id);
    try {
      await candidatesAPI.reparse(lead.id);
      toast.success(`${lead.name} scored — opening profile`);
      navigate(`/candidates/${lead.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not score this applicant');
      setBusyId(null);
    }
  };

  const dismiss = async (lead) => {
    setBusyId(lead.id);
    try {
      await leadsAPI.dismiss(lead.id);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success('Application dismissed');
    } catch (e) {
      toast.error('Could not dismiss');
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="leads-inbox-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", color: '#002A38' }}>
          <Inbox size={22} color="#0076A3" /> New Applications
          {leads.length > 0 && <span style={{ background: '#0076A3', color: '#fff', fontSize: '0.75rem', fontWeight: 700, borderRadius: 999, padding: '2px 10px' }} data-testid="inbox-count">{leads.length}</span>}
        </h1>
        <p className="text-sm text-slate-500 mt-1">Fresh applications from your careers pages. Review to score &amp; add them to your pipeline.</p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-10 text-center">Loading applications…</div>
      ) : leads.length === 0 ? (
        <Card data-testid="inbox-empty">
          <CardContent className="py-16 text-center text-slate-400">
            <Inbox size={40} className="mx-auto mb-3 opacity-50" />
            <div className="font-semibold text-slate-500" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>You're all caught up</div>
            <p className="text-sm mt-1">New applications from your careers pages will land here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => (
            <Card key={lead.id} data-testid="lead-card">
              <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{lead.name}</span>
                    <span style={{ background: '#EAF2FF', color: '#0076A3', fontSize: '0.6875rem', fontWeight: 700, borderRadius: 999, padding: '2px 8px' }}>via {lead.source}</span>
                    <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Clock size={11} /> {timeAgo(lead.applied_at)}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Briefcase size={12} /> {lead.job_title}</span>
                    {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-slate-700"><Mail size={12} /> {lead.email}</a>}
                    {lead.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {lead.phone}</span>}
                    {lead.filename && <span className="inline-flex items-center gap-1"><FileText size={12} /> {lead.filename}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setPreview(lead)} data-testid="lead-view-resume"><FileText size={14} className="mr-1.5" /> Resume</Button>
                  <Button size="sm" onClick={() => review(lead)} disabled={busyId === lead.id} data-testid="lead-review-btn" style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
                    <Sparkles size={14} className="mr-1.5" /> {busyId === lead.id ? 'Scoring…' : 'Review & Score'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => dismiss(lead)} disabled={busyId === lead.id} data-testid="lead-dismiss-btn" title="Dismiss"><Trash2 size={15} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent style={{ maxWidth: 640, background: '#fff' }} data-testid="lead-resume-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{preview?.name} · {preview?.filename || 'Resume'}</DialogTitle>
          </DialogHeader>
          <div style={{ maxHeight: '55vh', overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.8125rem', color: '#334155', lineHeight: 1.6, background: '#FAF7F2', border: '1px solid #ECE7DE', borderRadius: 10, padding: '1rem' }}>
            {preview?.resume_preview || 'No resume text captured.'}
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => { const l = preview; setPreview(null); review(l); }} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
              <Sparkles size={14} className="mr-1.5" /> Review & Score
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
